'use client';
import {memo, useCallback, useEffect, useRef} from 'react';
import {useStore} from 'jotai';
import {
  hoveredElementAtom,
  isDraggingNodeAtom,
  selectedElementAtom,
} from '../partialEdit/atoms';
import {useClearFrameSelection} from '../partialEdit/useFrameSelection';

interface SelectionOverlaysProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  chatId: string;
  hasPartialSelection: boolean;
  isInSelectMode: boolean;
  isActiveFrame: boolean;
  isSelected: boolean;
  isFrameNodeHovered: boolean;
}

/**
 * Overlay layer rendered in the light DOM on top of a frame's shadow-DOM content.
 * Four overlays, each positioned to track a DOM element inside the shadow root:
 *   - Selection outline (1px primary) for the currently selected element.
 *   - Parent dashed outline for the selected element's parent.
 *   - Hover outline (2px primary) for the element under the cursor while in
 *     partial-edit mode on the active frame.
 *   - Banani-button outlines shown on frame-node hover (for inactive frames),
 *     used as click targets for generate-next.
 *
 * Everything is positioned via `positionOverlay`, which converts
 * `getBoundingClientRect` from the shadow element into local pixels relative
 * to `containerRef`, compensating for the current canvas zoom scale.
 */
export const SelectionOverlays = memo(function SelectionOverlays({
  containerRef,
  chatId,
  hasPartialSelection,
  isInSelectMode,
  isActiveFrame,
  isSelected,
  isFrameNodeHovered,
}: SelectionOverlaysProps) {
  const selectionOverlayRef = useRef<HTMLDivElement>(null);
  const parentOverlayRef = useRef<HTMLDivElement>(null);
  const hoverOverlayRef = useRef<HTMLDivElement>(null);
  const bananiOverlaysRef = useRef<HTMLDivElement>(null);
  const bananiCleanupRef = useRef<(() => void) | null>(null);
  const bananiStateRef = useRef<BananiOverlayState | null>(null);
  const store = useStore();
  const clearFrameSelection = useClearFrameSelection();

  // Track the currently selected element and position the selection + parent
  // overlays. Also clears the atom if the element gets detached (e.g. after
  // a generation replaces the shadow content).
  useEffect(() => {
    const overlay = selectionOverlayRef.current;
    const parentOverlay = parentOverlayRef.current;
    const container = containerRef.current;
    if (!overlay || !parentOverlay || !container) return;
    if (!hasPartialSelection) {
      overlay.style.display = 'none';
      parentOverlay.style.display = 'none';
      return;
    }

    let resizeObserver: ResizeObserver | undefined;
    let observed: HTMLElement | undefined;
    let frameId: number | null = null;

    const reposition = (target: HTMLElement) => {
      positionOverlay(overlay, container, target);
      const parent = target.parentElement;
      if (parent && parent.isConnected) {
        positionOverlay(parentOverlay, container, parent, true);
      } else {
        parentOverlay.style.display = 'none';
      }
    };

    const scheduleReposition = (target: HTMLElement) => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        reposition(target);
      });
    };

    const update = () => {
      const selected = store.get(selectedElementAtom);
      const isMine = selected?.screenId === chatId;
      if (!selected || !isMine) {
        overlay.style.display = 'none';
        parentOverlay.style.display = 'none';
        resizeObserver?.disconnect();
        observed = undefined;
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
        return;
      }
      if (!selected.elementReference.isConnected) {
        // The selected DOM node is gone — most likely the screen content was
        // replaced (re-render/generation). Clear the atom so downstream
        // consumers (outline styles, hover suppression) return to the
        // no-selection state.
        overlay.style.display = 'none';
        parentOverlay.style.display = 'none';
        resizeObserver?.disconnect();
        observed = undefined;
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
        clearFrameSelection();
        return;
      }
      scheduleReposition(selected.elementReference);
      if (observed !== selected.elementReference) {
        resizeObserver?.disconnect();
        observed = selected.elementReference;
        resizeObserver = new ResizeObserver(() => {
          scheduleReposition(selected.elementReference);
        });
        resizeObserver.observe(selected.elementReference);
      }
    };

    update();
    const unsub = store.sub(selectedElementAtom, update);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      unsub();
      resizeObserver?.disconnect();
    };
  }, [store, chatId, hasPartialSelection, containerRef, clearFrameSelection]);

  // Hover overlay — only active frame, only in select mode, hidden while dragging.
  useEffect(() => {
    const overlay = hoverOverlayRef.current;
    const container = containerRef.current;
    if (!overlay || !container) return;
    if (!isInSelectMode || !isActiveFrame) {
      overlay.style.display = 'none';
      return;
    }

    let frameId: number | null = null;

    const scheduleUpdate = (target: HTMLElement) => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = null;
        positionOverlay(overlay, container, target);
      });
    };

    const update = () => {
      if (store.get(isDraggingNodeAtom)) {
        overlay.style.display = 'none';
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
        return;
      }
      const hovered = store.get(hoveredElementAtom);
      if (
        !hovered ||
        hovered.screenId !== chatId ||
        !hovered.elementReference.isConnected
      ) {
        overlay.style.display = 'none';
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
        return;
      }
      scheduleUpdate(hovered.elementReference);
    };

    update();
    const unsubHover = store.sub(hoveredElementAtom, update);
    const unsubDrag = store.sub(isDraggingNodeAtom, update);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      unsubHover();
      unsubDrag();
    };
  }, [store, chatId, isInSelectMode, isActiveFrame, containerRef]);

  const showBananiOverlays = useCallback(() => {
    const container = containerRef.current;
    const host = bananiOverlaysRef.current;
    if (!container || !host) return;

    const shadow = container.querySelector(
      '[data-banani-screenid]',
    )?.shadowRoot;
    const buttons = shadow?.querySelectorAll(
      '[data-media-type="banani-button"]',
    );
    if (!buttons || buttons.length === 0) return;

    const visibleButtons = Array.from(buttons).filter(
      (btn): btn is HTMLElement =>
        btn instanceof HTMLElement &&
        !(btn.offsetWidth === 0 && btn.offsetHeight === 0),
    );
    if (visibleButtons.length === 0) {
      host.style.display = 'none';
      return;
    }

    const nextSignature = visibleButtons
      .map(
        (btn) =>
          `${btn.tagName}:${btn.dataset.mediaAction || ''}:${btn.textContent || ''}`,
      )
      .join('|');
    const existingState = bananiStateRef.current;

    if (existingState?.signature !== nextSignature) {
      bananiCleanupRef.current?.();
      host.replaceChildren();

      const cleanups: (() => void)[] = [];
      const overlays: Array<{button: HTMLElement; overlay: HTMLDivElement}> =
        [];

      visibleButtons.forEach((btn) => {
        const div = document.createElement('div');
        div.className = 'banani-btn-overlay';
        host.appendChild(div);

        const onEnter = () => div.classList.add('hovered');
        const onLeave = () => div.classList.remove('hovered');
        btn.addEventListener('mouseenter', onEnter);
        btn.addEventListener('mouseleave', onLeave);
        cleanups.push(() => {
          btn.removeEventListener('mouseenter', onEnter);
          btn.removeEventListener('mouseleave', onLeave);
        });
        overlays.push({button: btn, overlay: div});
      });

      bananiCleanupRef.current = () => {
        cleanups.forEach((fn) => fn());
        bananiCleanupRef.current = null;
      };
      bananiStateRef.current = {
        signature: nextSignature,
        overlays,
      };
    }

    host.style.display = 'block';
    bananiStateRef.current?.overlays.forEach(({button, overlay}) => {
      if (!button.isConnected) return;
      positionOverlay(overlay, container, button);
    });
  }, [containerRef]);

  const hideBananiOverlays = useCallback(() => {
    bananiOverlaysRef.current?.style.setProperty('display', 'none');
  }, []);

  const cleanupBananiOverlays = useCallback(() => {
    bananiCleanupRef.current?.();
    bananiStateRef.current = null;
    bananiOverlaysRef.current?.replaceChildren();
  }, []);

  // Show/hide banani overlays based on FrameNode-level hover (inactive frames only).
  useEffect(() => {
    if (isFrameNodeHovered && !isActiveFrame && !isSelected) {
      showBananiOverlays();
    } else {
      hideBananiOverlays();
    }
    return () => cleanupBananiOverlays();
  }, [
    isFrameNodeHovered,
    isActiveFrame,
    isSelected,
    showBananiOverlays,
    hideBananiOverlays,
    cleanupBananiOverlays,
  ]);

  return (
    <>
      <div
        ref={parentOverlayRef}
        data-zoom-chrome='selection-overlay'
        className='dashed-overlay'
        style={PARENT_OVERLAY_STYLE}
      />
      <div
        ref={selectionOverlayRef}
        data-zoom-chrome='selection-overlay'
        style={SELECTION_OVERLAY_STYLE}
      />
      <div
        ref={hoverOverlayRef}
        data-zoom-chrome='selection-overlay'
        style={HOVER_OVERLAY_STYLE}
      />
      <div
        ref={bananiOverlaysRef}
        data-zoom-chrome='selection-overlay'
        style={BANANI_OVERLAYS_STYLE}
      />
    </>
  );
});

interface BananiOverlayState {
  signature: string;
  overlays: Array<{button: HTMLElement; overlay: HTMLDivElement}>;
}

const BASE_OVERLAY: React.CSSProperties = {
  position: 'absolute',
  display: 'none',
  pointerEvents: 'none',
};

const PARENT_OVERLAY_STYLE: React.CSSProperties = {
  ...BASE_OVERLAY,
  zIndex: 99998,
};

const SELECTION_OVERLAY_STYLE: React.CSSProperties = {
  ...BASE_OVERLAY,
  outline: 'calc(1px * var(--inverse-zoom, 1)) solid var(--primary-hex)',
  zIndex: 99999,
};

const HOVER_OVERLAY_STYLE: React.CSSProperties = {
  ...BASE_OVERLAY,
  outline: 'calc(2px * var(--inverse-zoom, 1)) solid var(--primary-hex)',
  zIndex: 99999,
};

const BANANI_OVERLAYS_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
};

/**
 * Position `overlay` to cover `target`, converting the target's viewport
 * rect into `container`-local coordinates and compensating for the current
 * canvas zoom scale (derived from containerRect.width / offsetWidth).
 *
 * `expandOutside` grows the overlay by one screen pixel on every side, so
 * 1px gradient borders (used by the dashed parent overlay) sit just outside
 * the target's edge instead of overlapping its content.
 */
function positionOverlay(
  overlay: HTMLDivElement,
  container: HTMLDivElement,
  target: HTMLElement,
  expandOutside = false,
) {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const scale = container.offsetWidth
    ? containerRect.width / container.offsetWidth
    : 1;
  if (!scale) return;
  const expand = expandOutside ? 1 / scale : 0;
  overlay.style.display = 'block';
  overlay.style.top = `${(targetRect.top - containerRect.top) / scale - expand}px`;
  overlay.style.left = `${(targetRect.left - containerRect.left) / scale - expand}px`;
  overlay.style.width = `${targetRect.width / scale + 2 * expand}px`;
  overlay.style.height = `${targetRect.height / scale + 2 * expand}px`;
}
